import { Country } from '@afterpay/types'
import { formatPercentage, valueIsNotNullOrUndefined } from '@afterpay/utils'
import { AllCountryCode } from '@afterpaytouch/core'
import { useFlag } from '@afterpaytouch/feature'
import { useRouter } from 'next/router'
import { AddressMode, Features, nonShippingInfoAddressModes, SupportedLocale } from '../../model'
import {
  useCheckoutState,
  useCountry,
  useCreditCheckConsented,
  useExperiment,
  useGetCreditCheckConsent,
  useGetTermsDisclosureStatus,
  useHasBillingAgreements,
  useIsBillingAgreementApprovalProcess,
  useIsDeferredShipping,
  useIsExpress,
  useShowPreferredPaymentDay,
  useCohort,
  useIsEstimatedPPA,
} from '../../state'
import { Cohort, TermsDisclosureStatus } from '@afterpaytouch/portal-api'
import { useMoney } from '../Money/useMoney'

interface LateFeeDetails {
  isZeroLateFee: boolean
  lateFee: string
}

interface TermsEventProps {
  ppa: string
  lateFee: boolean
  creditCheckConsent: boolean
  creditCheckConsented?: boolean
  termsDisclosureAgree?: boolean
  accountOpeningDisclosures?: boolean
}

export const useLateFeeDetails = (): LateFeeDetails => {
  const { started } = useCheckoutState()
  const locale = useRouter().locale as SupportedLocale
  const lateFeeCurrency = started?.firstLateFeeData?.fixedAmount?.currency ?? started?.consumerAmount.currency
  const { formatMoney } = useMoney({ locale, currency: lateFeeCurrency, decimalPlaces: 2 })

  const firstLateFee = started?.firstLateFeeData
  const zeroLateFee = valueIsNotNullOrUndefined(firstLateFee) ? firstLateFee.zeroLateFee : true
  const fixedAmount = firstLateFee?.fixedAmount?.amount ?? '0'
  const lateFeePercentage = firstLateFee?.variable ?? '0'
  const showFixedAmount = firstLateFee?.amountInCheckout ?? false

  const lateFee = showFixedAmount ? formatMoney(fixedAmount) : formatPercentage(lateFeePercentage)
  return {
    isZeroLateFee: zeroLateFee,
    lateFee,
  }
}

const isStandardOrBuyNowExpress = (isBuyNow: boolean, isExpress: boolean): boolean => {
  const isBuyNowExpress = isExpress && isBuyNow
  return !isExpress || isBuyNowExpress
}

const useShouldCreditCheck = (country: Country): boolean => {
  const isCreditCheckConsented = useCreditCheckConsented()
  const cohort = useCohort()
  const showCreditCheckConsentTerms = isCreditCheckConsented !== undefined && !isCreditCheckConsented && country === Country.US && cohort !== Cohort.CashOnly

  return showCreditCheckConsentTerms
}

export const useTermsEventProps = (): TermsEventProps => {
  const terms = useGetTerms()
  const creditCheckConsent = terms.toLowerCase().includes('creditcheckconsent')
  const creditCheckConsented = useCreditCheckConsented()
  const termsDisclosureAgree = useGetTermsDisclosureStatus() === TermsDisclosureStatus.NEW_USER_NOT_ACCEPTED_TERMS
  return {
    ppa: terms.includes('estimatedPPA') ? 'estimated' : 'standard',
    lateFee: terms.includes('lateFee'),
    creditCheckConsent,
    creditCheckConsented,
    termsDisclosureAgree: termsDisclosureAgree || undefined,
  }
}

// terms conditions

const isEstimatedPPAWithLateFeeTerms = (isZeroLateFee: boolean, hasEstimatedPPA: boolean): boolean => {
  return hasEstimatedPPA && !isZeroLateFee
}

const isEstimatedPPAWithNoLateFeeTerms = (isZeroLateFee: boolean, hasEstimatedPPA: boolean): boolean => {
  return hasEstimatedPPA && isZeroLateFee
}

const isNoEstimatedPPAWithLateFeeTerms = (isZeroLateFee: boolean, hasEstimatedPPA: boolean): boolean => {
  return !hasEstimatedPPA && !isZeroLateFee
}

const isNoEstimatedPPAWithNoLateFeeTerms = (isZeroLateFee: boolean, hasEstimatedPPA: boolean): boolean => {
  return !hasEstimatedPPA && isZeroLateFee
}

const isBillingAgreementApprovalProcessWithLateFeeTerms = (isZeroLateFee: boolean, isBillingAgreementApprovalProcess: boolean): boolean => {
  return isBillingAgreementApprovalProcess && !isZeroLateFee
}

const isBillingAgreementApprovalProcessWithNoLateFeeTerms = (isZeroLateFee: boolean, isBillingAgreementApprovalProcess: boolean): boolean => {
  return isBillingAgreementApprovalProcess && isZeroLateFee
}

const hasBillingAgreementWithLateFeeTerms = (isZeroLateFee: boolean, hasBillingAgreements: boolean): boolean => {
  return hasBillingAgreements && !isZeroLateFee
}

const hasBillingAgreementWithNoLateFeeTerms = (isZeroLateFee: boolean, hasBillingAgreements: boolean): boolean => {
  return hasBillingAgreements && isZeroLateFee
}

const isBillingAgreementTerms = (hasBillingAgreements: boolean): boolean => {
  return hasBillingAgreements
}

const isUSAndDeferredWithLateFeeTerms = (
  zeroLateFee: boolean,
  isDeferredShipping: boolean,
  country: AllCountryCode,
  addressMode: AddressMode | undefined,
  isExpress: boolean,
  isBuyNow: boolean
): boolean => {
  const isUS = country === Country.US
  const hasDeferredShipping = isDeferredShippingTerms(isDeferredShipping, isBuyNow, addressMode, isExpress)

  return hasDeferredShipping && !zeroLateFee && isUS
}

const isDeferredShippingTerms = (isDeferredShipping: boolean, isBuyNow: boolean, addressMode: AddressMode | undefined, isExpress: boolean): boolean => {
  const deferringShippingAddressMode = isDeferredShipping && !nonShippingInfoAddressModes.includes(addressMode)
  const isExpressWithoutBuyNow = !isBuyNow && isExpress

  return deferringShippingAddressMode || isExpressWithoutBuyNow
}

const isExpressNotDeferred = (isDeferredShipping: boolean, isExpress: boolean): boolean => {
  return isExpress && !isDeferredShipping
}

const showCreditCheckConsent = (nzCreditCheckEnabled: boolean, preferredDayShown: boolean, hasCreditCheckConsent?: boolean): boolean => {
  return nzCreditCheckEnabled && !hasCreditCheckConsent && hasCreditCheckConsent !== undefined && !preferredDayShown
}

const showUpdatedNZTerms = (termsEnabled: boolean, country: Country): boolean => {
  return termsEnabled && country === Country.NZ
}

const useNZCreditCheckEnabled = (): boolean => {
  const nzCreditCheck = useExperiment('NzCreditCheck')
  const nzCreditCheckEnabled = nzCreditCheck.active && !nzCreditCheck.control
  const country = useCountry()
  return nzCreditCheckEnabled && country === Country.NZ
}

export const useShowTermsDisclosure = (): boolean => {
  const country = useCountry()
  const termsDisclosureStatus = useGetTermsDisclosureStatus()
  return termsDisclosureStatus === TermsDisclosureStatus.NEW_USER_NOT_ACCEPTED_TERMS && country === Country.NZ
}

export const useGetTerms = (): string => {
  const country = useCountry()
  const isDeferredShipping = useIsDeferredShipping()
  const { initial } = useCheckoutState()
  const isExpress = useIsExpress()
  const cohort = useCohort()
  const enableBillingAgreementForCashCohort = useFlag(Features.ENABLE_BA_CASH_COHORT, false)
  const shouldShowBillingAgreementTerms = cohort === Cohort.CashOnly ? enableBillingAgreementForCashCohort : true
  const hasBillingAgreements = useHasBillingAgreements() && shouldShowBillingAgreementTerms
  const { isZeroLateFee } = useLateFeeDetails()
  const hasEstimatedPPA = useIsEstimatedPPA()
  const shouldCreditCheck = useShouldCreditCheck(country)
  const isBuyNow = initial.buyNow
  const isUSAndStandardOrBuyNowExpress = country === Country.US && isStandardOrBuyNowExpress(isBuyNow, isExpress)
  const hasPartialLateFeesEnabled = useFlag(Features.PARTIAL_LATE_FEES)
  const preferredDayShown = useShowPreferredPaymentDay()
  const isUKMarketRegsFCA = useFlag(Features.UK_MRKT_REGS_FCA)
  const hasCreditCheckConsent = useGetCreditCheckConsent()
  const isNZCreditCheckEnabled = useNZCreditCheckEnabled()
  const termsEnabled = useFlag(Features.NZ_UPDATED_TERMS)
  const showTermsDisclosure = useShowTermsDisclosure()
  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()

  // US only terms conditions
  if (isUSAndStandardOrBuyNowExpress) {
    if (isBillingAgreementApprovalProcessWithLateFeeTerms(isZeroLateFee, isBillingAgreementApprovalProcess)) {
      return shouldCreditCheck
        ? 'summary:terms.estimatedPPA.lateFee_billingAgreementApprovalProcessWithCreditCheckConsent'
        : 'summary:terms.estimatedPPA.lateFee_billingAgreementApprovalProcess'
    }

    if (isBillingAgreementApprovalProcessWithNoLateFeeTerms(isZeroLateFee, isBillingAgreementApprovalProcess)) {
      return shouldCreditCheck
        ? 'summary:terms.estimatedPPA.noLateFee_billingAgreementApprovalProcessWithCreditCheckConsent'
        : 'summary:terms.estimatedPPA.noLateFee_billingAgreementApprovalProcess'
    }

    if (hasBillingAgreementWithNoLateFeeTerms(isZeroLateFee, hasBillingAgreements)) {
      return shouldCreditCheck
        ? 'summary:terms.estimatedPPA.noLateFee_billingAgreementWithCreditCheckConsent'
        : 'summary:terms.estimatedPPA.noLateFee_billingAgreement'
    }

    if (hasBillingAgreementWithLateFeeTerms(isZeroLateFee, hasBillingAgreements)) {
      return shouldCreditCheck
        ? 'summary:terms.estimatedPPA.lateFee_billingAgreementWithCreditCheckConsent'
        : 'summary:terms.estimatedPPA.lateFee_billingAgreement'
    }

    if (isEstimatedPPAWithNoLateFeeTerms(isZeroLateFee, hasEstimatedPPA)) {
      return shouldCreditCheck ? 'summary:terms.estimatedPPA.noLateFee_creditCheckConsent' : 'summary:terms.estimatedPPA.noLateFee'
    }

    if (isNoEstimatedPPAWithNoLateFeeTerms(isZeroLateFee, hasEstimatedPPA)) {
      return shouldCreditCheck ? 'summary:terms.noEstimatedPPA.noLateFee_creditCheckConsent' : 'summary:terms.noEstimatedPPA.noLateFee'
    }

    if (isEstimatedPPAWithLateFeeTerms(isZeroLateFee, hasEstimatedPPA)) {
      return shouldCreditCheck ? 'summary:terms.estimatedPPA.lateFee_creditCheckConsent' : 'summary:terms.estimatedPPA.lateFee'
    }

    if (isNoEstimatedPPAWithLateFeeTerms(isZeroLateFee, hasEstimatedPPA)) {
      return shouldCreditCheck ? 'summary:terms.noEstimatedPPA.lateFee_creditCheckConsent' : 'summary:terms.noEstimatedPPA.lateFee'
    }
  }

  if (isUSAndDeferredWithLateFeeTerms(isZeroLateFee, isDeferredShipping, country, initial?.addressMode, isExpress, isBuyNow)) {
    return shouldCreditCheck ? 'summary:terms.lateFee_deferredShippingWithCreditCheckConsent' : 'summary:terms.lateFee_deferredShipping'
  }

  // everything else
  if (isBillingAgreementTerms(hasBillingAgreements)) {
    return showTermsDisclosure
      ? 'summary:terms_billingAgreement_termsNotAccepted'
      : showUpdatedNZTerms(termsEnabled, country)
      ? 'summary:terms_billingAgreement_updated'
      : hasPartialLateFeesEnabled
      ? buildPartialLateFeeText('summary:terms_billingAgreementPartialLateFees', preferredDayShown)
      : 'summary:terms_billingAgreement'
  }

  if (isUKMarketRegsFCA && isDeferredShippingTerms(isDeferredShipping, isBuyNow, initial?.addressMode, isExpress)) {
    return 'summary:termsFCA_deferredShipping'
  }

  if (isDeferredShippingTerms(isDeferredShipping, isBuyNow, initial?.addressMode, isExpress)) {
    return shouldCreditCheck
      ? 'summary:terms_deferredShippingWithCreditCheckConsent'
      : showTermsDisclosure
      ? 'summary:terms_deferredShipping_termsNotAccepted'
      : showUpdatedNZTerms(termsEnabled, country)
      ? 'summary:terms_deferredShipping_updated'
      : hasPartialLateFeesEnabled
      ? buildPartialLateFeeText('summary:terms_deferredShippingPartialLateFees', preferredDayShown)
      : 'summary:terms_deferredShipping'
  }

  if (isNZCreditCheckEnabled) {
    if (showTermsDisclosure) {
      return 'summary:terms_termsNotAccepted'
    }

    if (showCreditCheckConsent(isNZCreditCheckEnabled, preferredDayShown, hasCreditCheckConsent) && !isExpressNotDeferred(isDeferredShipping, isExpress)) {
      return 'summary:terms_creditCheckConsent'
    }

    if (showUpdatedNZTerms(termsEnabled, country)) {
      return preferredDayShown ? 'summary:terms_preferredDayShown' : 'summary:terms_updated'
    }
  }

  if (hasPartialLateFeesEnabled) {
    return buildPartialLateFeeText('summary:terms_partialLateFees', preferredDayShown)
  }

  if (isUKMarketRegsFCA) {
    return 'summary:termsFCA'
  }

  return 'summary:terms'
}

const buildPartialLateFeeText = (initialCopy: string, preferredDayShown: boolean): string => {
  const context = preferredDayShown ? '_preferredDayShown' : ''

  return `${initialCopy}${context}`
}
